import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TextField, CircularProgress, Skeleton } from '@mui/material';
import { ShieldCheck, Pencil, Save, Type, AlignLeft } from 'lucide-react';
import Policy_Navbar from './PolicyNavbar';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';

const BRAND = '#34C759';
const BRAND_SOFT = 'rgba(52,199,89,0.10)';
const BRAND_RING = 'rgba(52,199,89,0.45)';

// Premium green-accent outlined field styling
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        fontSize: '0.9rem',
        color: '#111827',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RING },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${BRAND_SOFT}` },
    },
};

const IosPolicy = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [policy, setPolicy] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [editedPolicy, setEditedPolicy] = useState({
        policy_title: '',
        policy_description: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const resp = await Calling.getIosPolicy();
            if (resp?.status) {
                setPolicy(resp?.data);
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedPolicy(prev => ({
            ...prev,
            [name]: value
        }));
    }

    const handleSave = async () => {
        setSaving(true);
        try {
            const resp = await Calling.updateIosPolicy(editedPolicy);
            if (resp?.status) {
                toast.success('Policy updated successfully');
                setPolicy(editedPolicy);
                setIsEditing(false);
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            console.log(err);
            toast.error('Failed to update policy');
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setEditedPolicy({
            policy_title: policy?.policy_title || '',
            policy_description: policy?.policy_description || ''
        });
    }, [policy]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full min-h-screen text-main-font"
        >
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-main-blue text-main-white shadow-sm shadow-main-blue/30">
                        <ShieldCheck size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">Privacy Policy <span className="text-sm font-semibold text-main-subfont align-middle">(iOS)</span></h1>
                        <p className="text-sm text-main-subfont">Manage iOS app privacy policy</p>
                    </div>
                </div>
            </div>

            {/* Main Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                {/* Navigation */}
                <div className="p-4 border-b border-main-border">
                    <Policy_Navbar
                        selectState={'iospolicy'}
                        onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                        label={saving ? 'Saving...' : (isEditing ? 'Save' : 'Edit')}
                        disabled={saving}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed ${isEditing
                            ? 'bg-main-blue text-main-white hover:bg-main-blue/90'
                            : 'bg-main-white text-main-delete border border-main-delete hover:bg-main-delete/5'
                            }`}
                        icon={saving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : (isEditing ? <Save size={16} /> : <Pencil size={16} />)}
                    />
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="space-y-4">
                            <Skeleton variant="rounded" height={32} width="40%" />
                            <Skeleton variant="rounded" height={300} />
                        </div>
                    ) : (
                        <>
                            {/* Editing Badge */}
                            {isEditing && (
                                <div className="mb-6 p-3 rounded-xl bg-main-th border border-main-blue/20 flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-main-blue animate-pulse"></div>
                                    <span className="text-sm text-main-font">
                                        <span className="font-medium text-main-blue">Edit Mode:</span> Make changes to your privacy policy below
                                    </span>
                                </div>
                            )}

                            {/* Title Section */}
                            <div className="mb-6">
                                {isEditing ? (
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-2 text-sm font-medium text-main-subfont">
                                            <Type size={16} />
                                            Policy Title
                                        </label>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="policy_title"
                                            value={editedPolicy.policy_title}
                                            onChange={handleInputChange}
                                            placeholder="Enter Privacy Policy Title"
                                            sx={fieldSx}
                                        />
                                    </div>
                                ) : (
                                    <div className="pb-4 border-b border-main-border">
                                        <h2 className="text-xl sm:text-2xl font-bold text-main-font break-words">
                                            {policy?.policy_title || 'No title set'}
                                        </h2>
                                    </div>
                                )}
                            </div>

                            {/* Description Section */}
                            <div>
                                {isEditing ? (
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-2 text-sm font-medium text-main-subfont">
                                            <AlignLeft size={16} />
                                            Policy Description
                                        </label>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={14}
                                            name="policy_description"
                                            value={editedPolicy.policy_description}
                                            onChange={handleInputChange}
                                            placeholder="Enter Privacy Policy Description"
                                            sx={fieldSx}
                                        />
                                    </div>
                                ) : (
                                    <div className="p-4 rounded-xl bg-main-bg border border-main-border">
                                        <pre className="text-sm sm:text-base text-main-font break-words whitespace-pre-wrap leading-relaxed font-sans">
                                            {policy?.policy_description || 'No description set'}
                                        </pre>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons (when editing) */}
                            {isEditing && (
                                <div className="mt-6 pt-6 border-t border-main-border flex justify-end gap-3">
                                    <button
                                        onClick={() => {
                                            setIsEditing(false);
                                            setEditedPolicy({
                                                policy_title: policy?.policy_title || '',
                                                policy_description: policy?.policy_description || ''
                                            });
                                        }}
                                        disabled={saving}
                                        className="px-5 py-2.5 rounded-xl font-medium text-main-font bg-main-white border border-main-border hover:bg-main-th transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-main-white bg-main-blue hover:bg-main-blue/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {saving ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <Save size={18} />}
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </motion.div>
    )
}

export default IosPolicy;
